/**
 * Platform registry settings shared by browser and server: display names,
 * super product names, family discounts and the per-firm default platforms
 * (Admin → Portfolios; Firestore `admin_portfolios/platforms-registry`).
 *
 * The registry arrives through a provider so deep synchronous readers never
 * await it: the server registers one over the primed catalog cache
 * (`adminPortfoliosRuntime.cjs`), the browser over the catalog API cache
 * (`portfoliosConfigService`). Without a provider the registry is empty and
 * every helper still answers (legacy HUB24 defaults, then null).
 *
 * CommonJS body in a `.js` file so server code can require it too. No object
 * rest/spread (see cjs-webpack-hazards).
 */

/**
 * Settings HUB24 had in code before they moved to the registry. They fill in
 * only when the stored platform entry has no value of its own, so a registry
 * saved before these fields existed keeps pricing and naming HUB24 exactly as
 * before. An explicit stored value (including a disabled discount) wins.
 */
const LEGACY_PLATFORM_DEFAULTS = {
  hub24: {
    label: 'HUB24',
    superProductName: 'HUB24 Super Choice',
    familyDiscount: { enabled: true, ratePercent: 10, capExtension: 541666.67 },
  },
};

const EMPTY_REGISTRY = { platforms: [], firmDefaults: {} };

let registryProvider = null;

function setPlatformRegistryProvider(provider) {
  registryProvider = typeof provider === 'function' ? provider : null;
}

function getPlatformRegistry() {
  if (!registryProvider) return EMPTY_REGISTRY;
  try {
    const reg = registryProvider();
    if (!reg) return EMPTY_REGISTRY;
    return {
      platforms: Array.isArray(reg.platforms) ? reg.platforms : [],
      firmDefaults: reg.firmDefaults && typeof reg.firmDefaults === 'object' ? reg.firmDefaults : {},
    };
  } catch (e) {
    return EMPTY_REGISTRY;
  }
}

function normaliseSlug(slug) {
  return String(slug || '').trim().toLowerCase();
}

function trimmed(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function registryOrDefault(registry) {
  return registry || getPlatformRegistry();
}

/** Stored entry merged over the legacy defaults for that slug (null when neither exists). */
function effectivePlatform(platform) {
  if (!platform) return null;
  const legacy = LEGACY_PLATFORM_DEFAULTS[normaliseSlug(platform.slug)];
  if (!legacy) return platform;
  const out = Object.assign({}, platform);
  if (!trimmed(out.superProductName) && legacy.superProductName) out.superProductName = legacy.superProductName;
  if (out.familyDiscount == null && legacy.familyDiscount) out.familyDiscount = Object.assign({}, legacy.familyDiscount);
  return out;
}

function findPlatformBySlug(registry, slug) {
  const key = normaliseSlug(slug);
  if (!key) return null;
  const reg = registryOrDefault(registry);
  const hit = (reg.platforms || []).find((p) => p && normaliseSlug(p.slug) === key);
  if (hit) return effectivePlatform(hit);
  const legacy = LEGACY_PLATFORM_DEFAULTS[key];
  return legacy ? effectivePlatform({ slug: key, label: legacy.label, fundNameDefault: legacy.label }) : null;
}

/** Platform whose label or default fund name is exactly `name` (trimmed, case-insensitive). */
function findPlatformByExactName(registry, name) {
  const key = trimmed(name).toLowerCase();
  if (!key) return null;
  const reg = registryOrDefault(registry);
  const hit = (reg.platforms || []).find((p) => p && (
    trimmed(p.label).toLowerCase() === key || trimmed(p.fundNameDefault).toLowerCase() === key
  ));
  if (hit) return effectivePlatform(hit);
  const legacySlug = Object.keys(LEGACY_PLATFORM_DEFAULTS)
    .find((slug) => LEGACY_PLATFORM_DEFAULTS[slug].label.toLowerCase() === key);
  return legacySlug ? findPlatformBySlug({ platforms: [] }, legacySlug) : null;
}

function platformDisplayName(platform) {
  if (!platform) return '';
  return trimmed(platform.label) || trimmed(platform.fundNameDefault) || trimmed(platform.slug);
}

/** "HUB24 Super Choice", else "<display name> Super". */
function platformSuperProductName(platform) {
  if (!platform) return '';
  const eff = effectivePlatform(platform);
  const explicit = trimmed(eff.superProductName);
  if (explicit) return explicit;
  const name = platformDisplayName(eff);
  return name ? `${name} Super` : '';
}

/**
 * `{ rate, capExtension }` (rate as a fraction) when the platform offers a
 * family discount, else null.
 */
function platformFamilyDiscount(platform) {
  if (!platform) return null;
  const cfg = effectivePlatform(platform).familyDiscount;
  if (!cfg || cfg.enabled !== true) return null;
  const pct = Number(cfg.ratePercent);
  if (!Number.isFinite(pct) || pct <= 0 || pct >= 100) return null;
  const ext = Number(cfg.capExtension);
  return {
    rate: pct / 100,
    ratePercent: pct,
    capExtension: Number.isFinite(ext) && ext > 0 ? ext : null,
  };
}

function isActivePlatform(platform) {
  return !!platform && platform.archived !== true;
}

/** The advisory team (firm) a client belongs to — same key as server/lib/advisoryTeam.cjs. */
function clientFirmId(clientData) {
  const id = clientData && clientData.assignment ? clientData.assignment.assignedAdvisory : null;
  return trimmed(String(id || '')) || null;
}

/**
 * The firm's default recommended / alternative platform slugs. Slugs that are
 * missing from the registry or archived are ignored.
 */
function resolveFirmPlatformDefaults(registry, teamId) {
  const reg = registryOrDefault(registry);
  const row = teamId && reg.firmDefaults ? reg.firmDefaults[teamId] : null;
  const pick = (slug) => {
    const key = normaliseSlug(slug);
    if (!key) return null;
    const hit = (reg.platforms || []).find((p) => p && normaliseSlug(p.slug) === key);
    return hit && isActivePlatform(hit) ? key : null;
  };
  return {
    recommendedPlatform: row ? pick(row.recommendedPlatform) : null,
    alternativePlatform: row ? pick(row.alternativePlatform) : null,
  };
}

/** Every platform display name / default fund name in the registry (plus legacy names). */
function allPlatformNames(registry) {
  const reg = registryOrDefault(registry);
  const names = [];
  (reg.platforms || []).forEach((p) => {
    [p && p.label, p && p.fundNameDefault].forEach((n) => {
      const t = trimmed(n);
      if (t && names.indexOf(t) === -1) names.push(t);
    });
  });
  Object.keys(LEGACY_PLATFORM_DEFAULTS).forEach((slug) => {
    const n = LEGACY_PLATFORM_DEFAULTS[slug].label;
    if (names.indexOf(n) === -1) names.push(n);
  });
  return names;
}

module.exports = {
  LEGACY_PLATFORM_DEFAULTS,
  setPlatformRegistryProvider,
  getPlatformRegistry,
  effectivePlatform,
  findPlatformBySlug,
  findPlatformByExactName,
  platformDisplayName,
  platformSuperProductName,
  platformFamilyDiscount,
  isActivePlatform,
  clientFirmId,
  resolveFirmPlatformDefaults,
  allPlatformNames,
};
